import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/shadcn/components/ui/sheet";
import { useNearByFilters, useI18N } from "common/hooks";
import { IoArrowBackOutline } from "react-icons/io5";
import { StandardButton } from "@/components/common/StandardButton";
import type { FilterSectionResult } from "common/types";
import AccessibilityCollapse from "@/components/FiltersSheet/components/AccessibilityCollapse";
import { sectionPanel, selectionOf, withSelection } from "@/lib/accessibilityFilterState";
import { encodeSelection, type SelectionKey } from "common/utils/accessibilitySelection";

type NearbyFiltersSheetProps = {
  open: boolean;
  onClose: () => void;
  nearbyFilterAccessibility: any[];
  /** When non-empty, the section list replaces `nearbyFilterAccessibility`. */
  nearbyFilterSections?: FilterSectionResult[];
};

function NearbyFiltersSheet({
  open,
  onClose,
  nearbyFilterAccessibility,
  nearbyFilterSections,
}: NearbyFiltersSheetProps) {
  const i18n = useI18N();

  const { filtersState, setFiltersState } = useNearByFilters();
  const [statusMessage, setStatusMessage] = useState("");

  // An empty list means nothing nearby recorded a filter feature; the full list beats an empty sheet.
  const sectionFilter = useMemo(() => {
    const sections =
      nearbyFilterSections && nearbyFilterSections.length > 0 ? nearbyFilterSections : nearbyFilterAccessibility;
    return sections && sections.length > 0 ? sectionPanel(sections) : null;
  }, [nearbyFilterSections, nearbyFilterAccessibility]);
  const [sectionKeys, setSectionKeys] = useState<SelectionKey[]>([]);
  const [seededOpen, setSeededOpen] = useState(false);
  // Ticks stay local until Apply, starting again from the shared selection on every open.
  if (open !== seededOpen) {
    setSeededOpen(open);
    if (open) setSectionKeys(selectionOf(filtersState));
  }

  const [localBusinessTypes, setLocalBusinessTypes] = useState<string[]>();

  useEffect(() => {
    setLocalBusinessTypes(filtersState.selectedBusinessTypes);
  }, [filtersState.selectedBusinessTypes]);

  const handleClose = () => {
    onClose();
  };

  const handleClearAll = () => {
    setSectionKeys([]);
    setStatusMessage(i18n.get("filtersCleared"));
    setFiltersState((prev) => ({
      ...withSelection(prev, []),
      openingHoursSelected: false,
      isFiltersApplied: false,
      filtersApplied: null,
      selectedBusinessTypes: [],
    }));
  };

  const handleSetFilters = () => {
    const types = (localBusinessTypes ?? []).join("|");
    const { accessibilities } = encodeSelection(sectionKeys);
    const showVenueOptions = filtersState?.openingHoursSelected ? "Open Now" : "All";
    const isFiltersApplied =
      types.length > 0 || accessibilities.length > 0 || !!filtersState?.openingHoursSelected;

    setFiltersState((prev) => ({
      ...withSelection(prev, sectionKeys),
      isFiltersApplied,
      filtersApplied: isFiltersApplied ? { types, accessibilities, showVenueOptions } : null,
      selectedBusinessTypes: localBusinessTypes ?? [],
    }));
    setStatusMessage(i18n.get(isFiltersApplied ? "filtersApplied" : "noFiltersApplied"));
    handleClose();
  };

  const firstFocusFilterRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (open && firstFocusFilterRef.current) {
      console.log("open", open);
      // Delay focusing to ensure the element is rendered
      setTimeout(() => {
        console.log("drawerMenuOpen focus set");

        firstFocusFilterRef.current.focus();
      }, 1000);
    }
  }, [open]); // Trigger the effect when the drawer menu opens

  return (
    <Sheet open={open} onOpenChange={() => handleClose()}>
      <SheetContent
        showClose={false}
        side="left"
        className="w-full bg-bg-brand-secondary-default pl-4 flex flex-col h-screen"
      >
        <SheetTitle className="sr-only">Filters Sheet</SheetTitle>
        <div className="flex flex-col h-full overflow-hidden mb-10">
          {/* Add a visually hidden live region to announce filter status changes */}
          <div aria-live="polite" className="sr-only" role="status">
            {statusMessage}
          </div>
          <StandardButton
            variant="backListView"
            onClick={() => handleClose()}
            className="flex flex-row justify-start items-center"
            aria-label={`Back to ${i18n.get("listview")}`}
          >
            <IoArrowBackOutline size={24} aria-hidden="true" focusable="false" />
            <div className="ml-2 body-l-semibold text-fg-neutral-strong">
              {i18n.get("listview")}
            </div>
          </StandardButton>
          <section className="mt-[20px]">
            <h2 className="text-fg-neutral-strong title-s-semibold mb-4">
              <span
                tabIndex={-1}
                ref={firstFocusFilterRef} // Attach the ref here
              >
                {i18n.get("filters")}
              </span>
            </h2>
          </section>

          {/* Main content area - always scrollable */}
          <div className="flex-1 overflow-y-auto">
            <section className="">
              {sectionFilter?.preparedData.map((section) =>
                section.accessibility_tags.length > 0 ? (
                  <AccessibilityCollapse
                    key={section.id}
                    label={section.name}
                    sectionId={section.id}
                    kind={section.kind}
                    tagsToShow={section.accessibility_tags}
                    tagSections={sectionFilter.tagSections}
                    allSections={sectionFilter.allSections}
                    tagsToAdd={sectionFilter.tagsToAdd}
                    complexSectionIds={sectionFilter.complexSectionIds}
                    headingLevel={2}
                    keys={sectionKeys}
                    onKeysChange={setSectionKeys}
                  />
                ) : null,
              )}
            </section>
          </div>
          <div className="absolute left-0 right-0 bottom-0 flex flex-row justify-between items-center px-[20px] bg-bg-brand-secondary-default py-4">
            <StandardButton
              variant="link"
              className="text-links title-s-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring-default)]"
              onClick={handleClearAll}
            >
              {i18n.get("clearAll")}
            </StandardButton>
            <StandardButton
              onClick={handleSetFilters}
              variant="primary"
              size="lg"
              className="px-6 py-3 rounded-xl text-white bg-fg-neutral-strong title-s-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring-default)]"
            >
              {i18n.get("applyFilters")}
            </StandardButton>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default NearbyFiltersSheet;
